import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayCircle, TrendingUp, Clock, Award } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import useAuthStore from '../../stores/authStore';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsResponse, sessionsResponse] = await Promise.all([
        api.get('/api/user/stats'),
        api.get('/api/user/sessions?limit=5'),
      ]);

      setStats(statsResponse.data.stats);
      setRecentSessions(sessionsResponse.data.sessions);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      console.error('Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      toast.error(`Ошибка загрузки данных: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Добро пожаловать, {user?.firstName || 'Пользователь'}!
        </h1>
        <p className="text-gray-600 mt-1">Продолжайте тренировки и совершенствуйте навыки продаж</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <PlayCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Всего сессий</h3>
          <p className="text-3xl font-bold text-gray-900">{stats?.totalSessions || 0}</p>
          <p className="text-sm text-gray-500 mt-2">
            Завершено: {stats?.completedSessions || 0}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Award className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Средний балл</h3>
          <p className="text-3xl font-bold text-gray-900">
            {Math.round(stats?.averageScore || 0)}%
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {stats?.averageScore >= 70 ? 'Отличный результат!' : 'Продолжайте тренироваться'}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общее время</h3>
          <p className="text-3xl font-bold text-gray-900">
            {Math.round((stats?.totalDuration || 0) / 60000)} мин
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Средняя сессия: {Math.round((stats?.averageDuration || 0) / 60000)} мин
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Прогресс</h3>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.completedSessions > 0 ? '+' : ''}
            {stats?.completedSessions || 0}
          </p>
          <p className="text-sm text-gray-500 mt-2">Завершенных тренировок</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Быстрые действия</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => navigate('/scenarios')}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl p-6 transition-all hover:shadow-lg"
          >
            <PlayCircle className="w-8 h-8 mb-3" />
            <h3 className="text-lg font-semibold mb-1">Начать тренировку</h3>
            <p className="text-sm text-blue-100">Выберите сценарий и начните практику</p>
          </button>

          <button
            onClick={() => navigate('/history')}
            className="bg-white border-2 border-gray-200 hover:border-blue-300 rounded-xl p-6 transition-all hover:shadow-lg"
          >
            <Clock className="w-8 h-8 text-gray-700 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">История сессий</h3>
            <p className="text-sm text-gray-600">Просмотрите прошлые тренировки</p>
          </button>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Последние тренировки</h2>
          <button
            onClick={() => navigate('/history')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Смотреть все →
          </button>
        </div>
        <div className="p-6">
          {recentSessions.length === 0 ? (
            <div className="text-center py-8">
              <PlayCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">У вас пока нет тренировок</p>
              <button
                onClick={() => navigate('/scenarios')}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Начать первую тренировку
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{session.scenario_name}</h4>
                    <p className="text-sm text-gray-500">
                      {new Date(session.start_time).toLocaleString('ru-RU')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {session.score !== null && session.score !== undefined
                          ? `${session.score}%`
                          : session.status === 'active' ? 'В процессе' : 'Без оценки'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {session.duration_ms
                          ? `${Math.round(session.duration_ms / 60000)} мин`
                          : '—'}
                      </p>
                    </div>
                    {session.status === 'completed' && session.score !== null && session.score !== undefined && (
                      <div
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          session.score >= 70
                            ? 'bg-green-100 text-green-700'
                            : session.score >= 50
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {session.score >= 70 ? 'Отлично' : session.score >= 50 ? 'Хорошо' : 'Нужна практика'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
